import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Image, Wand2, Star } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface PublishInstagramModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MediaItem {
  id: string;
  name: string;
  image: string;
  folder: string;
  isMain?: boolean;
}

const PublishInstagramModal: React.FC<PublishInstagramModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<MediaItem[]>([]);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Mock media data - in real app this would come from API
  const mediaItems = [
    { id: '1', name: 'Imagen 1', image: '/placeholder-image.jpg', folder: 'Mi catálogo' },
    { id: '2', name: 'Imagen 2', image: '/placeholder-image.jpg', folder: 'Mis modelos IA' },
    { id: '3', name: 'Imagen 3', image: '/placeholder-image.jpg', folder: 'Mi catálogo' },
    { id: '4', name: 'Imagen 4', image: '/placeholder-image.jpg', folder: 'Mis videos IA' },
    { id: '5', name: 'Imagen 5', image: '/placeholder-image.jpg', folder: 'Mi catálogo' },
    { id: '6', name: 'Imagen 6', image: '/placeholder-image.jpg', folder: 'Mis modelos IA' },
    { id: '7', name: 'Imagen 7', image: '/placeholder-image.jpg', folder: 'Mi catálogo' },
    { id: '8', name: 'Imagen 8', image: '/placeholder-image.jpg', folder: 'Mis videos IA' },
  ];

  const filteredItems = mediaItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.folder.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleItemSelect = (item: MediaItem) => {
    if (selectedItems.length < 20 && !selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(prev => [...prev, item]);
    }
  };

  const handleItemRemove = (itemId: string) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleSetMain = (itemId: string) => {
    setSelectedItems(prev => prev.map(item => ({
      ...item,
      isMain: item.id === itemId
    })));
  };

  const handleGenerate = async () => {
    if (selectedItems.length === 0) {
        alert(t('selectAtLeastOneImage'));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate generation progress
    const interval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsGenerating(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                {t('publishToInstagram')}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                  placeholder={t('searchImagesPlaceholder')}
                />
              </div>

              {/* Selected Items */}
              {selectedItems.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    {t('selectedImages')} ({selectedItems.length}/20)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {selectedItems.map((item, index) => (
                      <div key={item.id} className="relative">
                        <div className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                          item.isMain
                            ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                            : 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        }`}>
                          <div className="w-full h-16 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-2">
                            <Image className="w-6 h-6 text-gray-500" />
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-300">{item.folder}</p>
                            {item.isMain && (
                              <div className="flex items-center justify-center mt-1">
                                <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                <span className="text-xs text-yellow-600 dark:text-yellow-400 ml-1">{t('mainImage')}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <button
                              onClick={() => handleSetMain(item.id)}
                              className={`p-1 rounded-lg transition-colors duration-200 ${
                                item.isMain
                                  ? 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'
                                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700'
                              }`}
                            >
                              <Star size={12} className={item.isMain ? 'fill-current' : ''} />
                            </button>
                            <button
                              onClick={() => handleItemRemove(item.id)}
                              className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Items */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('imageFileArchive')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredItems.map((item) => {
                    const isSelected = selectedItems.find(i => i.id === item.id);
                    const canSelect = selectedItems.length < 20;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemSelect(item)}
                        disabled={!!isSelected || !canSelect}
                        className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : canSelect
                            ? 'border-gray-300 dark:border-slate-600 hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-slate-700'
                            : 'border-gray-200 dark:border-slate-700 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div className="w-full h-20 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center mb-2">
                          <Image className="w-8 h-8 text-gray-500" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {item.name}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">{item.folder}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center">
                            <span className="text-xs">✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Prompt Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Instrucciones específicas para el caption
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="input min-h-[100px] resize-none"
                  placeholder="Describe el tono, hashtags, información del producto, o cualquier detalle específico que quieras incluir en el caption de Instagram..."
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Las instrucciones ayudarán a la IA a generar captions más atractivos y relevantes.
                </p>
              </div>

              {/* Generation Progress */}
              {isGenerating && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                    <span>Generando captions con IA...</span>
                    <span>{generationProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${generationProgress}%` }}
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-slate-700">
                <button
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={isGenerating}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || selectedItems.length === 0}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Wand2 size={16} />
                  <span>{isGenerating ? 'Generando...' : 'Generar captions'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublishInstagramModal;
